import React, {
	FC,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useSearchParams } from "react-router-dom";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { UserContext, TranslationContext } from "@/app/providers";
import { useTaskPage, useTaskTableHeaderActions } from "@/features/task/hooks";
import { TaskGroup } from "@/features/task/model/Task/TaskRequest";
import {
	AddNewTaskDialog,
	EditTaskDialog,
	TaskDialog,
	useTaskHeaderActions,
	useTaskHeaderPrimaryAction,
} from "@/features/task/ui/components";
import { CustomToolbarV2, StyledDataGrid } from "@/shared/data-grid";
import { isImageAttachment } from "@/shared/lib/attachmentPreview";
import { DataGridCommentTooltip } from "@/shared/ui/DataGridCommentTooltip";
import { ImageAttachmentPreviewDialog } from "@/shared/ui/ImageAttachmentPreviewDialog";
import Spinner from "@/shared/ui/Spinner";
import ComponentError from "@/shared/ui/errors/Components";
import { useSelectedTaskGroups } from "@/storage/useSelectedTaskGroups";
import {
	sharedAutoHeightDataGridSectionSx,
	sharedTableCheckboxSx,
	sharedTableTagChipSx,
} from "@/styles/sharedDataGridStyles";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import SearchIcon from "@mui/icons-material/Search";
import {
	Alert,
	Box,
	Button,
	Card,
	Checkbox,
	Chip,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	Link,
	MenuItem,
	Select,
	Snackbar,
	TablePagination,
	TextField,
	Tooltip,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import rehypeRaw from "rehype-raw";

import { SortDirection } from "@/model/SortDirection";
import { UserSummary } from "@/model/User/UserSummary";

import {
	checkAdminAccess,
	useFetchUserSingle,
} from "@/service/ApplicationQueries";
import { useFetchShiftSystems } from "@/service/ShiftHomeQueries";
import {
	useFetchTaskById,
	useFetchTaskGroups,
	useFetchTasks,
} from "@/service/TaskQueries";
import { useFetchUserSummariesByCurrentCompany } from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";


/* ───────────────────────── pastel palette to match previous screens ───────────────────────── */
export const COL = {
	cardBg: (t: Theme) => t.palette.background.paper,
	cardBorder: (t: Theme) => t.palette.divider,
	stripe: "#E5E7EB",
	hover: (t: Theme) => (t.palette.mode === "light" ? "#F7F7F9" : "#1f2937"),
	chipBg: "#F1F5F9",
	tagBg: "#EEF2FF",
	tagText: "#3730A3",
	danger: "#FCA5A5",
	warning: "#FDE68A",
	ok: "#86EFAC",
};

interface TaskAttachmentDto {
	Url?: string;
	FileName?: string;
}

interface TaskCommentDto {
	Text?: string;
	Value?: string;
	Created?: string | Date;
	Date?: string | Date;
}

interface TaskUserDto {
	Id?: number;
	Name?: string;
}

interface TaskRecurringConfigurationDto {
	Frequency?: string;
}

interface TaskDto {
	TaskId: number;
	TaskGroupId?: number;
	Title: string;
	Deadline?: string | Date | null;
	Description?: string;
	Status?: string;
	Created?: string | Date | null;
	Reccuring?: boolean;
	ReccuringConfiguration?: TaskRecurringConfigurationDto | null;
	Shiftlog?: boolean;
	Handover?: boolean;
	User?: TaskUserDto | null;
	Tags?: string[];
	Attachment?: TaskAttachmentDto | null;
	Comments?: TaskCommentDto[];
	GroupResponsibleList?: number[];
	UserResponsibleList?: number[];
}

interface TaskGroupsResponse {
	Groups: TaskGroup[];
}

interface TasksResponse {
	Items: TaskDto[];
	Total: number;
	Page: number;
	PageSize: number;
	HasNextPage: boolean;
}

interface LegacyTasksResponse {
	Tasks?: TaskDto[];
	Total?: number;
}

interface TaskRow {
	id: number;
	title: string;
	deadline?: string | Date | null;
	description: string;
	status: string;
	created: Date | null;
	recurring: "Yes" | "No";
	frequency: string;
	shiftlog: "Yes" | "No";
	handover: "Yes" | "No";
	user: string;
	tags: string[];
	attachment: TaskAttachmentDto | null;
	comments: TaskCommentDto[];
	groupName: string;
	groupResponsibleCount: number;
	userResponsibleCount: number;
	responsibleGroups: string[];
	responsibleUsers: string[];
}

interface TaskCellParams {
	row: TaskRow;
	value?: unknown;
}

type TaskGroupsPayload = TaskGroupsResponse | TaskGroup[];
type TasksPayload = TasksResponse | LegacyTasksResponse;

const isTaskGroupsResponse = (
	payload: TaskGroupsPayload | undefined,
): payload is TaskGroupsResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		!Array.isArray(payload) &&
		Array.isArray(payload.Groups),
	);

const isTasksResponse = (
	payload: TasksPayload | undefined,
): payload is TasksResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		"Items" in payload &&
		Array.isArray(payload.Items),
	);

const isLegacyTasksResponse = (
	payload: TasksPayload | undefined,
): payload is LegacyTasksResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		"Tasks" in payload &&
		Array.isArray(payload.Tasks),
	);

interface TaskCardProps {
	title: string;
}

const formatDisplayName = (name?: string) => {
	const trimmedName = name?.trim();
	if (!trimmedName) return "";

	return trimmedName
		.toLowerCase()
		.replace(/(^|[\s._-])([a-z])/g, (_, separator: string, letter: string) => {
			return `${separator}${letter.toUpperCase()}`;
		});
};

const TaskCard: FC<TaskCardProps> = ({ title }) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:767px)");
	const taskPage = useTaskPage();
	const [searchParams, setSearchParams] = useSearchParams();
	const hasTaskPage = Boolean(taskPage);
	const setTaskHeaderActions = useTaskHeaderActions();
	const setTaskHeaderPrimaryAction = useTaskHeaderPrimaryAction();
	const sharedSearchInput = taskPage?.searchInput;
	const persistedTaskGroups = useSelectedTaskGroups();
	const selectedGroupIds = taskPage?.selectedGroupIds ?? persistedTaskGroups[0];
	const setSelectedGroupIds =
		taskPage?.setSelectedGroupIds ?? persistedTaskGroups[1];

	// Permissions
	const user = useContext(UserContext);
	useFetchUserSingle(user?.Id); // keep call for side-effects/permissions
	const isAdmin = Boolean(user && checkAdminAccess(user));

	// State: Persistent Selected Groups
	// Dialogs & UI state
	const [taskDialogOpen, setTaskDialogOpen] = useState(false);
	const [selectedTask, setSelectedTask] = useState<TaskRow | null>(null);
	const [openNewTaskDialog, setOpenNewTaskDialog] = useState(false);
	const [taskIdToEdit, setTaskIdToEdit] = useState<number | null>(null);
	const [imageAttachmentPreview, setImageAttachmentPreview] = useState<{
		url: string;
		fileName: string;
	} | null>(null);
	const [showGroupWarning, setShowGroupWarning] = useState(false);
	const [searchTermTable, setSearchTermTable] = useState("");
	const [searchInput, setSearchInput] = useState(searchTermTable);
	const [newComment, setNewComment] = useState("");

	// Table pagination (1‑based page for API, synced with DataGrid server pagination)
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	// Sort state
	const [sortDirection, setSortDirection] = useState<SortDirection>(
		SortDirection.ASC,
	);

	// Defaults for new task dialog
	const [groupsNewTask, setGroupsNewTask] = useState<string[]>([
		"Administration",
	]);

	// Fetches
	const { data: usersData = [] } = useFetchUserSummariesByCurrentCompany();
	const users: UserSummary[] = usersData;
	const { data: shiftSystems = [] } = useFetchShiftSystems();
	const { data: taskGroupsData } = useFetchTaskGroups() as {
		data?: TaskGroupsPayload;
	};
	const taskGroupsAvaliable = useMemo(() => {
		if (Array.isArray(taskGroupsData)) {
			return taskGroupsData;
		}

		if (isTaskGroupsResponse(taskGroupsData)) {
			return taskGroupsData.Groups;
		}

		return [];
	}, [taskGroupsData]);
	const { data: detailedTask, isLoading: isLoadingTaskDetail } =
		useFetchTaskById(taskIdToEdit ?? 0);

	// Stable dep for fetchTasks (avoid re-renders)
	const stableGroupIds = useMemo(
		() =>
			selectedGroupIds
				.map((groupId) => Number(groupId))
				.filter((groupId): groupId is number => Number.isFinite(groupId)),
		[selectedGroupIds.join(",")],
	);

	const taskResults = useFetchTasks(
		stableGroupIds,
		"Active",
		page,
		pageSize,
		searchTermTable,
		sortDirection,
	) as {
		data?: TasksPayload;
		isFetching: boolean;
	};
	const { data, isFetching } = taskResults;

	useEffect(() => {
		setShowGroupWarning(selectedGroupIds.length === 0);
	}, [selectedGroupIds]);

	useEffect(() => {
		if (sharedSearchInput !== undefined) {
			setSearchInput(sharedSearchInput);
			setSearchTermTable(sharedSearchInput);
			setPage(1);
		}
	}, [sharedSearchInput]);

	const useHeaderGridActions = hasTaskPage && isAdmin;

	useEffect(() => {
		if (!hasTaskPage || !isAdmin) {
			setTaskHeaderPrimaryAction(null);
			return () => setTaskHeaderPrimaryAction(null);
		}

		setTaskHeaderPrimaryAction({
			label: "New Task",
			onClick: () => setOpenNewTaskDialog(true),
		});

		return () => setTaskHeaderPrimaryAction(null);
	}, [hasTaskPage, isAdmin, setTaskHeaderPrimaryAction]);

	// Transform tasks → rows
	const { timezone } = useTime();
	const userNameById = useMemo(
		() => new Map(users.map((candidate) => [candidate.Id, candidate.Name])),
		[users],
	);

	const mapTaskToDialogRow = useCallback(
		(task: TaskDto | null | undefined) => {
			if (!task) return null;

			const group = taskGroupsAvaliable.find(
				(candidate) => candidate.Id === task.TaskGroupId,
			);
			const groupResponsibleIds = task.GroupResponsibleList?.length
				? task.GroupResponsibleList
				: typeof task.TaskGroupId === "number"
					? [task.TaskGroupId]
					: [];
			const userResponsibleIds = task.UserResponsibleList?.length
				? task.UserResponsibleList
				: task.User?.Id
					? [task.User.Id]
					: [];

			const responsibleGroupNames = groupResponsibleIds
				.map(
					(groupId) =>
						taskGroupsAvaliable.find((candidate) => candidate.Id === groupId)
							?.Name,
				)
				.filter((name): name is string => Boolean(name));

			const responsibleUserNames = userResponsibleIds
				.map((userId) => userNameById.get(userId))
				.map((name) => formatDisplayName(name))
				.filter((name): name is string => Boolean(name));
			const assignedUserName = formatDisplayName(
				(task.User?.Id ? userNameById.get(task.User.Id) : undefined) ??
					task.User?.Name,
			);

			return {
				id: task.TaskId,
				title: task.Title ?? "",
				deadline: task.Deadline,
				description: task.Description ?? "",
				status: task.Status ?? "",
				created:
					task.Created && dayjs(task.Created).isValid()
						? dayjs(task.Created).toDate()
						: null,
				recurring: task.Reccuring ? "Yes" : "No",
				frequency: task.ReccuringConfiguration?.Frequency ?? "",
				shiftlog: task.Shiftlog ? "Yes" : "No",
				handover: task.Handover ? "Yes" : "No",
				user: assignedUserName,
				tags: task.Tags ?? [],
				attachment: task.Attachment ?? null,
				comments: task.Comments ?? [],
				groupName: group?.Name ?? "",
				groupResponsibleCount: groupResponsibleIds.length,
				userResponsibleCount: userResponsibleIds.length,
				responsibleGroups: responsibleGroupNames,
				responsibleUsers: responsibleUserNames,
			} satisfies TaskRow;
		},
		[taskGroupsAvaliable, userNameById],
	);

	const taskItems = useMemo<TaskDto[]>(() => {
		if (isTasksResponse(data)) return data.Items;
		if (isLegacyTasksResponse(data)) return data.Tasks ?? [];
		return [];
	}, [data]);

	const rows = useMemo(() => {
		return taskItems
			.map((task) => mapTaskToDialogRow(task))
			.filter((row): row is TaskRow => row !== null);
	}, [taskItems, mapTaskToDialogRow]);

	const totalItems =
		typeof data?.Total === "number" ? data.Total : taskItems.length;
	const fileNameExport = useMemo(
		() => `ActiveTasks_${dayjs().format("YYYY-MM-DD_HH-mm")}`,
		[],
	);
	const taskIdFromUrl = useMemo(() => {
		const raw = searchParams.get("taskId");
		if (!raw) return null;
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	}, [searchParams]);
	const taskByIdData = useFetchTaskById(taskIdFromUrl ?? 0) as {
		data?: TaskDto;
	};
	const { data: selectedTaskById } = taskByIdData;
	const hydratedTaskById = useMemo(
		() => mapTaskToDialogRow(selectedTaskById),
		[selectedTaskById, mapTaskToDialogRow],
	);
	const getTaskRowSignature = useCallback(
		(task: TaskRow | null | undefined) =>
			task
				? JSON.stringify({
						id: task.id,
						title: task.title,
						description: task.description,
						status: task.status,
						deadline: task.deadline ?? null,
						lastChanged: String(task.lastChanged ?? ""),
						tags: task.tags,
						groupName: task.groupName,
						frequency: task.frequency,
						shiftlog: task.shiftlog,
						handover: task.handover,
						responsibleGroups: task.responsibleGroups,
						responsibleUsers: task.responsibleUsers,
					})
				: "",
		[],
	);

	const syncTaskIdInUrl = (taskId: number | null) => {
		const next = new URLSearchParams(searchParams);
		if (taskId == null) next.delete("taskId");
		else next.set("taskId", String(taskId));
		setSearchParams(next, { replace: true });
	};

	const setTaskDialogState = (open: boolean) => {
		setTaskDialogOpen(open);
		if (!open) {
			syncTaskIdInUrl(null);
			return;
		}
		if (selectedTask?.id) {
			syncTaskIdInUrl(Number(selectedTask.id));
		}
	};

	const openTaskDialogForRow = (row: TaskRow) => {
		setSelectedTask(row);
		setTaskDialogOpen(true);
		if (row?.id) {
			syncTaskIdInUrl(Number(row.id));
		}
	};

	const handleGroupChange = (event: SelectChangeEvent<number[]>) => {
		const value = event.target.value;
		const groupIds = Array.isArray(value)
			? value.map((id) => Number(id)).filter((id) => Number.isFinite(id))
			: [];
		setSelectedGroupIds(groupIds);
		setPage(1);
	};

	useEffect(() => {
		if (taskIdFromUrl == null) {
			if (taskDialogOpen) {
				setTaskDialogOpen(false);
			}
			return;
		}

		const matchedRow = rows.find((row) => Number(row.id) === taskIdFromUrl);
		const preferredTask = hydratedTaskById ?? matchedRow;
		if (!preferredTask) return;

		if (
			!taskDialogOpen ||
			Number(selectedTask?.id) !== taskIdFromUrl ||
			getTaskRowSignature(selectedTask) !== getTaskRowSignature(preferredTask)
		) {
			setSelectedTask(preferredTask);
			setTaskDialogOpen(true);
		}
	}, [
		hydratedTaskById,
		getTaskRowSignature,
		rows,
		taskIdFromUrl,
		taskDialogOpen,
		selectedTask,
	]);

	// ── Highlight & text helpers ─────────────────────────────────────────────
	const highlight = (text?: string, term?: string) => {
		const t = (text ?? "").toString();
		const q = (term ?? "").trim().toLowerCase();
		if (!q) return t;
		const idx = t.toLowerCase().indexOf(q);
		if (idx === -1) return t;

		const before = t.slice(0, idx);
		const match = t.slice(idx, idx + q.length);
		const after = t.slice(idx + q.length);

		return (
			<>
				{before}
				<Box
					component="mark"
					sx={{
						px: 0.25,
						borderRadius: 0.5,
						backgroundColor: alpha(theme.palette.warning.main, 0.28),
					}}
				>
					{match}
				</Box>
				{after}
			</>
		);
	};

	const stripHtml = (s?: string) =>
		s
			? s
					.replace(/<[^>]*>/g, " ")
					.replace(/\s+/g, " ")
					.trim()
			: "";

	const truncate = (s: string, n = 160) =>
		s.length > n ? `${s.slice(0, n - 1)}…` : s;

	const fmt = (d?: string | number | Date | dayjs.Dayjs | null) =>
		d ? dayjs.utc(d).tz(timezone).format("DD/MM/YY HH:mm:ss") : "";

	const getDeadlineFromFormatterInput = (
		input: unknown,
	): TaskRow["deadline"] => {
		if (!input || typeof input !== "object") return null;
		const params = input as { row?: TaskRow; value?: TaskRow["deadline"] };
		return params.row?.deadline ?? params.value ?? null;
	};

	// helper near the columns
	const asCommentsArray = (row: TaskRow & { Comments?: TaskCommentDto[] }) =>
		Array.isArray(row?.comments)
			? row.comments
			: Array.isArray(row?.Comments)
				? row.Comments
				: [];

	const renderMobileTaskCard = (row: TaskRow) => {
		const deadlineLabel = row.deadline ? fmt(row.deadline) : "No deadline";
		const tags = Array.isArray(row?.tags) ? row.tags : [];
		const quickFlags = [
			row.handover === "Yes" ? "Handover" : null,
			row.shiftlog === "Yes" ? "Shiftlog" : null,
			row.recurring === "Yes" ? "Recurring" : null,
		].filter(Boolean) as string[];

		return (
			<Card
				key={`mobile-${row.id}`}
				variant="outlined"
				onClick={() => openTaskDialogForRow(row)}
				sx={{
					borderRadius: 2,
					borderColor: alpha(theme.palette.divider, 0.9),
					bgcolor: "background.paper",
					cursor: "pointer",
					transition: "background-color 120ms ease, border-color 120ms ease",
					"&:active": {
						bgcolor: alpha(theme.palette.primary.main, 0.04),
					},
				}}
			>
				<Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1 }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "flex-start",
							justifyContent: "space-between",
							gap: 1,
						}}
					>
						<Box sx={{ minWidth: 0, flex: 1 }}>
							<Typography
								variant="subtitle2"
								sx={{
									fontWeight: 800,
									display: "-webkit-box",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									overflow: "hidden",
								}}
							>
								{highlight(row.title, searchInput)}
							</Typography>
						</Box>
						<Box
							sx={{
								width: 32,
								height: 32,
								display: "grid",
								placeItems: "center",
								borderRadius: 2,
								bgcolor: alpha(theme.palette.primary.main, 0.08),
								color: "primary.main",
								flexShrink: 0,
							}}
						>
							<ArrowOutwardIcon sx={{ fontSize: 18 }} />
						</Box>
					</Box>

					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
						}}
					>
						{truncate(stripHtml(row.description) || "No description", 120)}
					</Typography>

					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
						<Chip
							size="small"
							label={`Deadline ${deadlineLabel}`}
							sx={{ borderRadius: 2, maxWidth: "100%" }}
						/>
						<Chip
							size="small"
							label={row.user || "Unassigned"}
							sx={{ borderRadius: 2, maxWidth: "100%" }}
						/>
					</Box>

					{(quickFlags.length > 0 || tags.length > 0) && (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
							{quickFlags.map((flag) => (
								<Chip
									key={`${row.id}-${flag}`}
									size="small"
									label={flag}
									sx={{
										borderRadius: 2,
										bgcolor: alpha(theme.palette.primary.main, 0.08),
										color: "primary.main",
									}}
								/>
							))}
							{tags.slice(0, 2).map((tag: string) => (
								<Chip
									key={`${row.id}-${tag}`}
									size="small"
									label={tag}
									sx={{ borderRadius: 2 }}
								/>
							))}
							{tags.length > 2 && (
								<Chip
									size="small"
									label={`+${tags.length - 2}`}
									sx={{ borderRadius: 2 }}
								/>
							)}
						</Box>
					)}
				</Box>
			</Card>
		);
	};

	// Columns
	const columns = useMemo(
		() => [
			{
				field: "deadline",
				headerName: "Deadline",
				flex: 1.1,
				headerAlign: "left",
				align: "left",
				headerClassName: "leftAlignedColumnHeader",
				cellClassName: "leftAlignedCell",
				sortable: true,
				valueFormatter: (params: unknown) =>
					fmt(getDeadlineFromFormatterInput(params)),

				renderCell: ({ row }: TaskCellParams) => {
					if (!row.deadline) {
						return <Typography variant="body2">—</Typography>;
					}

					const now = dayjs();
					const deadline = dayjs.utc(row.deadline).tz(timezone);
					const isPast = deadline.isBefore(now);
					const isSoon = !isPast && deadline.isBefore(now.add(24, "hour"));
					const diffHours = Math.abs(deadline.diff(now, "hour"));
					const statusLabel = isPast
						? "Overdue"
						: isSoon
							? "Due soon"
							: "Scheduled";
					const statusColor = isPast
						? theme.palette.error.main
						: isSoon
							? theme.palette.warning.dark
							: theme.palette.primary.main;

					return (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								minWidth: 0,
								width: "100%",
								height: "100%",
								py: 0.125,
								textAlign: "left",
							}}
						>
							<Typography
								variant="caption"
								fontWeight={700}
								color="text.primary"
								sx={{ lineHeight: 1.15, letterSpacing: 0.1 }}
							>
								{deadline.format("DD/MM/YY HH:mm")}
							</Typography>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.4,
									mt: 0.3,
									minWidth: 0,
								}}
							>
								<Typography
									variant="caption"
									noWrap
									sx={{ fontWeight: 600, color: statusColor, lineHeight: 1 }}
								>
									{statusLabel}
								</Typography>
							</Box>
						</Box>
					);
				},
			},
			{
				field: "title",
				headerName: "Title",
				flex: 2.2,
				headerAlign: "left",
				align: "left",
				headerClassName: "leftAlignedColumnHeader",
				cellClassName: "leftAlignedCell",
				sortable: false,
				renderCell: ({ row }: TaskCellParams) => (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							minWidth: 0,
							width: "100%",
							height: "100%",
							py: 0.125,
							textAlign: "left",
						}}
					>
						<Typography
							variant="body2"
							fontWeight={700}
							noWrap
							sx={{ lineHeight: 1.25 }}
						>
							{highlight(row.title, searchTermTable)}
						</Typography>
					</Box>
				),
			},
				{
					field: "description",
					headerName: "Description",
					flex: 2.2,
					headerAlign: "left",
					align: "left",
					headerClassName: "leftAlignedColumnHeader",
					cellClassName: "leftAlignedCell",
					sortable: false,
				renderCell: ({ value, row }: TaskCellParams) => {
					const rawValue = typeof value === "string" ? value : "";
					const hasSearch = !!searchTermTable.trim();
					if (hasSearch) {
						const plain = stripHtml(rawValue);
						const preview = truncate(plain, 220);
						return (
							<Tooltip title={highlight(plain, searchTermTable)} arrow>
								<Box sx={{ width: "100%", minWidth: 0, textAlign: "left" }}>
									<Typography
										variant="body2"
										sx={{
											whiteSpace: "normal",
											display: "-webkit-box",
											WebkitLineClamp: 3,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
											textAlign: "left",
										}}
									>
										{highlight(preview, searchTermTable)}
									</Typography>
								</Box>
							</Tooltip>
						);
					}
					const plain = stripHtml(rawValue);
					const preview = truncate(plain, 220);
					return (
						<Tooltip
							title={
								<SATOSContentRender
									isNotProcedureRender={true}
									rehypePlugins={[rehypeRaw]}
									content={rawValue}
								/>
							}
							arrow
						>
							<Box sx={{ minWidth: 0, width: "100%", textAlign: "left" }}>
								<Typography
									variant="body2"
									sx={{
										display: "-webkit-box",
										WebkitLineClamp: 3,
										WebkitBoxOrient: "vertical",
										overflow: "hidden",
										lineHeight: 1.45,
										textAlign: "left",
									}}
								>
									{preview || "No description"}
								</Typography>
								{row?.attachment?.FileName ? (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ mt: 0.35, display: "block", textAlign: "left" }}
									>
										Attachment: {row.attachment.FileName}
									</Typography>
								) : null}
							</Box>
						</Tooltip>
					);
				},
			},
				{
					field: "created",
					headerName: "Created",
					flex: 1.2,
					sortable: false,
					valueFormatter: ({ value }: TaskCellParams) =>
						fmt(
							typeof value === "string" || value instanceof Date ? value : null,
						),
					renderCell: ({ value }: TaskCellParams) => {
						const parsedValue =
							typeof value === "string" || value instanceof Date ? value : null;
						return (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									height: "100%",
									minWidth: 0,
									width: "100%",
									py: 0.125,
									textAlign: "center",
								}}
							>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ lineHeight: 1.15 }}
								>
									{parsedValue
										? dayjs.utc(parsedValue).tz(timezone).format("DD/MM/YY HH:mm")
										: "—"}
								</Typography>
							</Box>
						);
					},
				},
			{
				field: "recurring",
				headerName: "Recurring",
				flex: 0.8,
				sortable: false,
				renderCell: (params: TaskCellParams) => (
					<Checkbox
						checked={params.value === "Yes"}
						disabled
						size="small"
						sx={sharedTableCheckboxSx(theme)}
					/>
				),
			},
			{
				field: "attachment",
				headerName: "Attachment",
				flex: 1.4,
				sortable: false,
				renderCell: ({ row }: TaskCellParams) =>
					row.attachment?.Url ? (
						<Link
							href={row.attachment.Url}
							target={isImageAttachment(row.attachment) ? undefined : "_blank"}
							rel="noopener noreferrer"
							onClick={(event) => {
								if (!isImageAttachment(row.attachment)) return;
								event.preventDefault();
								setImageAttachmentPreview({
									url: row.attachment?.Url ?? "",
									fileName: row.attachment?.FileName ?? "Image attachment",
								});
							}}
							underline="hover"
							sx={{
								display: "inline-flex",
								alignItems: "center",
								minWidth: 0,
								overflow: "hidden",
							}}
						>
							<ArrowOutwardIcon fontSize="small" sx={{ mr: 0.5 }} />
							<Typography
								variant="body2"
								noWrap
								sx={{ textOverflow: "ellipsis" }}
							>
								{highlight(row.attachment.FileName ?? "", searchTermTable)}
							</Typography>
						</Link>
					) : (
						<Typography variant="body2" color="text.disabled">
							—
						</Typography>
					),
			},
			{
				field: "comments",
				headerName: "Comments",
				flex: 1.4,
				sortable: false,

				// 1) Make CSV/print/export get a flat string
				valueGetter: ({ row }: TaskCellParams) => {
					const list = asCommentsArray(row);
					if (!list.length) return "";
					return list
						.map((comment) => {
							const text = stripHtml(comment?.Text ?? comment?.Value ?? "");
							const when =
								comment?.Created || comment?.Date
									? dayjs
											.utc(comment.Created ?? comment.Date)
											.tz(timezone)
											.format("DD/MM/YY HH:mm:ss")
									: "";
							return when ? `${text} (${when})` : text;
						})
						.join(" | ");
				},

				// 2) UI must use params.row (NOT params.value) because value is now a string
				renderCell: ({ row }: TaskCellParams) => {
					const list = asCommentsArray(row);
					if (!list.length) {
						return (
							<Typography variant="caption" color="text.disabled">
								<Translate String="Auto.Features.Task.Pages.ActiveTasks.L1021C60.NoComments">No comments</Translate>
							</Typography>
						);
					}

					const hasSearch = !!searchTermTable.trim();
					const tooltipItems = list.map((comment, index) => {
						const when =
							comment?.Date || comment?.Created
								? dayjs(comment.Date ?? comment.Created).format(
										"DD/MM/YY HH:mm:ss",
									)
								: "Unknown";

						if (hasSearch) {
							const plain = stripHtml((comment.Value ?? comment.Text) || "");
							const preview =
								plain.length > 220 ? `${plain.slice(0, 219)}…` : plain;

							return (
								<Box key={index}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
									>
										{when}
									</Typography>
									<Typography variant="body2" sx={{ whiteSpace: "normal" }}>
										{highlight(preview, searchTermTable)}
									</Typography>
								</Box>
							);
						}

						return (
							<Box key={index}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
								>
									{when}
								</Typography>
								<Box
									sx={{
										fontSize: theme.typography.body2.fontSize,
										lineHeight: 1.45,
										"& p": { m: 0 },
										"& p + p": { mt: 0.5 },
										"& *": { maxWidth: "100%" },
										"& a": {
											wordBreak: "break-word",
											overflowWrap: "anywhere",
										},
									}}
								>
									<SATOSContentRender
										isNotProcedureRender={true}
										rehypePlugins={[rehypeRaw]}
										content={comment.Value ?? comment.Text}
									/>
								</Box>
							</Box>
						);
					});

					return <DataGridCommentTooltip items={tooltipItems} />;
				},
			},
			{
				field: "shiftlog",
				headerName: "Shiftlog",
				flex: 0.8,
				sortable: false,
				renderCell: (params: TaskCellParams) => (
					<Checkbox
						checked={params.value === "Yes"}
						disabled
						size="small"
						sx={sharedTableCheckboxSx(theme)}
					/>
				),
			},
			{
				field: "handover",
				headerName: "Handover",
				flex: 0.8,
				sortable: false,
				renderCell: (params: TaskCellParams) => (
					<Checkbox
						checked={params.value === "Yes"}
						disabled
						size="small"
						sx={sharedTableCheckboxSx(theme)}
					/>
				),
			},
			{
				field: "tags",
				headerName: "Tags",
				flex: 1.6,
				sortable: false,
				renderCell: ({ row }: TaskCellParams) => (
					<Box
						sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, minWidth: 0 }}
					>
						{row.tags.map((tag: string, idx: number) => (
							<Chip
								key={idx}
								label={
									<Typography variant="caption" noWrap>
										{highlight(tag, searchTermTable)}
									</Typography>
								}
								size="small"
								sx={sharedTableTagChipSx(theme)}
							/>
						))}
					</Box>
				),
			},
			{
				field: "user",
				headerName: "Name",
				flex: 1,
				headerAlign: "left",
				align: "left",
				headerClassName: "leftAlignedColumnHeader",
				cellClassName: "leftAlignedCell",
				sortable: false,
				renderCell: ({ row, value }: TaskCellParams) => (
					<Box sx={{ minWidth: 0, width: "100%", textAlign: "left" }}>
						<Typography variant="body2" fontWeight={700} noWrap>
							{highlight(
								typeof value === "string" ? value : "Unassigned",
								searchTermTable,
							)}
						</Typography>
						<Typography variant="caption" color="text.secondary" noWrap>
							{row.userResponsibleCount ?? 0} <Translate String="Auto.Features.Task.Pages.ActiveTasks.L1158C39.User">user</Translate>
							{row.userResponsibleCount === 1 ? "" : "s"} •{" "}
							{row.groupResponsibleCount ?? 0} <Translate String="Auto.Features.Task.Pages.ActiveTasks.L1160C40.Group">group</Translate>
							{row.groupResponsibleCount === 1 ? "" : "s"}
						</Typography>
					</Box>
				),
			},
		],
		[timezone, searchTermTable, theme],
	);
	const { apiRef, pdfDialog, toolbarProps } = useTaskTableHeaderActions({
		setTaskHeaderActions,
		isMobile,
		rows,
		columns,
		fileName: fileNameExport,
		enableGridTools: useHeaderGridActions,
	});

	return (
		<Card
			elevation={0}
			sx={{
				width: "100%",
				flex: 1,
				minHeight: 0,
				borderRadius: 2,
				border: (t) => `1px solid ${t.palette.divider}`,
				m: { xs: 1, sm: 1.5, md: 2 },
				bgcolor: COL.cardBg(theme),
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<Box
				sx={{
					p: 2,
					pb: 0,
					flexShrink: 0,
				}}
			>
				{/* Top Controls */}
				<Box
					sx={{
						pt: taskPage ? 0 : 0.5,
						pb: taskPage ? 0 : 1.5,
						bgcolor: COL.cardBg(theme),
						flexShrink: 0,
					}}
				>
					{!taskPage && (
						<Grid container spacing={1.5} alignItems="center">
							{isAdmin && !taskPage && (
								<Grid item xs={12} sm="auto">
									<Button
										variant="contained"
										color="info"
										onClick={() => setOpenNewTaskDialog(true)}
										sx={{ textTransform: "none", fontWeight: 800 }}
									>
										+ <Translate String="Tasks.NewTask">New Task</Translate>
									</Button>
								</Grid>
							)}

							{!taskPage && (
								<Grid item xs={12} md={4}>
									<FormControl
										fullWidth
										size="small"
										variant="outlined"
										error={selectedGroupIds.length === 0}
										sx={{
											"& .MuiOutlinedInput-root": { borderRadius: 2 },
										}}
									>
										<InputLabel>
											<Translate String="Tasks.Group">Group</Translate>
										</InputLabel>
										<Select
											disabled={!taskGroupsAvaliable.length}
											multiple
											value={selectedGroupIds}
											onChange={handleGroupChange}
											label={<Translate String="Tasks.Group">Group</Translate>}
											renderValue={(selected) => (
												<Box
													sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
												>
													{(Array.isArray(selected) ? selected : []).map(
														(id) => {
															const group = taskGroupsAvaliable.find(
																(candidate) => candidate.Id === Number(id),
															);
															return group ? (
																<Chip
																	key={group.Id}
																	label={group.Name}
																	size="small"
																	sx={{ bgcolor: COL.chipBg, fontWeight: 700 }}
																/>
															) : null;
														},
													)}
												</Box>
											)}
											MenuProps={{
												PaperProps: {
													elevation: 3,
													sx: { borderRadius: 2, mt: 0.5 },
												},
											}}
										>
											{taskGroupsAvaliable.map((group) => (
												<MenuItem key={group.Id} value={group.Id}>
													{group.Name}
												</MenuItem>
											))}
										</Select>
										{selectedGroupIds.length === 0 && (
											<FormHelperText>
												<Translate String="Tasks.SelectAtLeastOne">
													Please select at least one group
												</Translate>
											</FormHelperText>
										)}
									</FormControl>
								</Grid>
							)}

							{!taskPage && (
								<Grid item xs={12} md>
									<Box display="flex" gap={1}>
										<TextField
											fullWidth
											size="small"
											variant="outlined"
											disabled={selectedGroupIds.length === 0 || isFetching}
											label={
												<Translate String="Tasks.SearchTasks">
													Search Tasks
												</Translate>
											}
											value={searchInput}
											onChange={(e) => setSearchInput(e.target.value)}
											sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
										/>
										<Button
											startIcon={<SearchIcon />}
											variant="outlined"
											color="primary"
											disabled={selectedGroupIds.length === 0 || isFetching}
											onClick={() => {
												setSearchTermTable(searchInput);
												setPage(1);
											}}
											sx={{ borderRadius: 2, fontWeight: 800, minWidth: 120 }}
										>
											<Translate String="Common.Search">Search</Translate>
										</Button>
									</Box>
								</Grid>
							)}
						</Grid>
					)}

					<Snackbar
						open={showGroupWarning}
						autoHideDuration={3000}
						onClose={() => setShowGroupWarning(false)}
						anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
					>
						<Alert
							onClose={() => setShowGroupWarning(false)}
							severity="warning"
							sx={{ width: "100%" }}
						>
							<Translate String="Tasks.SelectAtLeastOne">
								Please select at least one group.
							</Translate>
						</Alert>
					</Snackbar>
				</Box>
			</Box>

			<Box
				sx={{
					px: 2,
					pb: 2,
					flex: 1,
					minHeight: 0,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
				}}
			>
				{selectedGroupIds.length === 0 ? (
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							display: "grid",
							placeItems: "center",
							textAlign: "center",
							color: "text.secondary",
						}}
					>
						<Box>
							<Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
								<Translate String="Tasks.SelectGroupTitle">
									Select a group to see tasks
								</Translate>
							</Typography>
							<Typography variant="body2">
								<Translate String="Tasks.SelectGroupDescription">
									Choose one or more groups from the filter above to load active
									tasks.
								</Translate>
							</Typography>
						</Box>
					</Box>
				) : (
					<>
						<Card
							variant="outlined"
							sx={{
								flex: 1,
								minHeight: 0,
								borderRadius: 2,
								overflow: "hidden",
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box
								sx={{
									...sharedAutoHeightDataGridSectionSx(theme),
								}}
							>
								<ErrorBoundary
									fallback={({ error, resetError }) => {
										const safeReset = () => {
											if (typeof resetError === "function") {
												(resetError as () => void)();
											}
										};
										return (
											<ComponentError
												message={
													error instanceof Error
														? error.message
														: "Something went wrong."
												}
												reset={safeReset}
											/>
										);
									}}
								>
									<Suspense fallback={<Spinner text={"Loading"} />}>
										{isMobile ? (
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													gap: 1,
													p: 0.25,
												}}
											>
												{rows.map((row) => renderMobileTaskCard(row))}
											</Box>
										) : (
											<StyledDataGrid
												apiRef={apiRef}
												autoHeight
												rows={rows}
												rowCount={totalItems}
												columns={columns}
												showToolbar={isAdmin}
												hideFooterPagination
												disableRowSelectionOnClick
												disableVirtualization
												loading={isFetching}
												getRowHeight={() => "auto"}
												sortingMode="server"
												sortingOrder={["asc", "desc"]}
												sortModel={[
													{
														field: "deadline",
														sort:
															sortDirection === SortDirection.DESC
																? "desc"
																: "asc",
													},
												]}
												onSortModelChange={(model) => {
													if (
														model.length > 0 &&
														model[0].field === "deadline"
													) {
														setSortDirection(
															model[0].sort === "desc"
																? SortDirection.DESC
																: SortDirection.ASC,
														);
														setPage(1);
													}
												}}
												getRowClassName={(params) => {
													const index = params.indexRelativeToCurrentPage ?? 0;
													return index % 2 === 0 ? "row-even" : "row-odd";
												}}
												onRowClick={(params) => {
													openTaskDialogForRow(params.row as TaskRow);
												}}
												slots={
													isAdmin ? { toolbar: CustomToolbarV2 } : undefined
												}
												slotProps={
													useHeaderGridActions
														? { toolbar: toolbarProps }
														: undefined
												}
													sx={{
														width: "100%",
														maxWidth: "100%",
														"& .MuiDataGrid-columnHeader": {
															justifyContent: "center",
														},
														"& .MuiDataGrid-columnHeaderTitleContainer": {
															justifyContent: "center",
														},
														"& .MuiDataGrid-columnHeaderTitleContainerContent": {
															justifyContent: "center",
															width: "100%",
														},
														"& .MuiDataGrid-columnHeaderTitle": {
															width: "100%",
															textAlign: "center",
														},
														"& .MuiDataGrid-cell": {
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															textAlign: "center",
														},
														"& .leftAlignedColumnHeader": {
															justifyContent: "flex-start",
														},
														"& .leftAlignedColumnHeader .MuiDataGrid-columnHeaderTitleContainer": {
															justifyContent: "flex-start",
														},
														"& .leftAlignedColumnHeader .MuiDataGrid-columnHeaderTitleContainerContent": {
															justifyContent: "flex-start",
														},
														"& .leftAlignedColumnHeader .MuiDataGrid-columnHeaderTitle": {
															textAlign: "left",
														},
														"& .leftAlignedCell": {
															justifyContent: "flex-start",
															textAlign: "left",
														},
													}}
												/>
										)}
									</Suspense>
								</ErrorBoundary>

								{!isFetching && rows.length === 0 && (
									<Box
										sx={{
											position: "absolute",
											inset: 0,
											display: "grid",
											placeItems: "center",
											textAlign: "center",
											color: "text.secondary",
											pointerEvents: "none",
											px: 3,
										}}
									>
										<Box>
											<Typography
												variant="h6"
												sx={{ fontWeight: 800, mb: 0.5 }}
											>
												<Translate String="Tasks.NoResults">
													No results
												</Translate>
											</Typography>
											<Typography variant="body2">
												<Translate String="Tasks.TryAdjustingFilters">
													Try adjusting your filters or search query.
												</Translate>
											</Typography>
										</Box>
									</Box>
								)}
							</Box>
						</Card>

						{totalItems > 0 && (
							<TablePagination
								component="div"
								count={totalItems}
								page={page - 1}
								onPageChange={(_event, newPage) => setPage(newPage + 1)}
								rowsPerPage={pageSize}
								onRowsPerPageChange={(event) => {
									const newSize = Number(event.target.value);
									if (newSize !== pageSize) {
										setPageSize(newSize);
										setPage(1);
									}
								}}
								rowsPerPageOptions={[10, 25, 50, 100]}
								SelectProps={{ size: "small" }}
								labelRowsPerPage={
									<Translate String="Shift.PaginationRows">
										Rows per page:
									</Translate>
								}
								labelDisplayedRows={({ from, to, count }) =>
									count !== -1 ? `${from}–${to} of ${count}` : `${from}–${to}`
								}
								showFirstButton
								showLastButton
								sx={{
									flexShrink: 0,
									borderTop: (t) => `1px solid ${t.palette.divider}`,
									bgcolor: "background.default",
									minHeight: 28,
									"& .MuiTablePagination-toolbar": {
										minHeight: 28,
										px: 1.5,
										py: 0,
									},
									"& .MuiTablePagination-selectLabel": {
										mr: 0.5,
										display: { xs: "none", sm: "block" },
									},
									"& .MuiTablePagination-displayedRows": {
										fontSize: { xs: "0.75rem", sm: "inherit" },
									},
									"& .MuiTablePagination-select": {
										py: 0,
										px: 0.75,
										minHeight: 22,
									},
									"& .MuiTablePagination-actions": { ml: 0 },
									"& .MuiIconButton-root": {
										padding: 2,
										"& svg": { fontSize: 16 },
									},
								}}
							/>
						)}
					</>
				)}
			</Box>

			{/* Edit Task Dialog */}
			<EditTaskDialog
				open={!!taskIdToEdit && !!detailedTask && !isLoadingTaskDetail}
				handleClose={() => setTaskIdToEdit(null)}
				onSaved={(savedTaskId) => {
					setTaskIdToEdit(null);
					syncTaskIdInUrl(savedTaskId);
					setTaskDialogOpen(true);
				}}
				title={GetLanguageValueAsString(translation, "Auto.Features.Task.Pages.ActiveTasks.L1594C11.EditTask", "Edit Task")}
				taskId={taskIdToEdit!}
				detailedTask={detailedTask}
				groupOptions={taskGroupsAvaliable || []}
				userOptions={users || []}
				systemOptions={shiftSystems}
			/>

			{/* New Task Dialog */}
			<AddNewTaskDialog
				key={openNewTaskDialog ? "open" : "closed"}
				open={openNewTaskDialog}
				handleClose={() => setOpenNewTaskDialog(false)}
				groupOptions={taskGroupsAvaliable || []}
				systemOptions={shiftSystems || []}
				userOptions={users || []}
				title={title}
				groupsNewTask={groupsNewTask}
				setGroupsNewTask={setGroupsNewTask}
			/>

			{/* Task Info Dialog */}
			<TaskDialog
				taskDialogOpen={taskDialogOpen}
				selectedTask={selectedTask}
				setTaskDialogOpen={setTaskDialogState}
				renderInfoItem={(label, value) => (
					<Box>
						<Typography variant="subtitle2" color="textSecondary">
							{label}
						</Typography>
						<Typography>{value}</Typography>
					</Box>
				)}
				newComment={newComment}
				setNewComment={setNewComment}
				isAdmin={isAdmin}
				setTaskIdToEdit={setTaskIdToEdit}
			/>
			<ImageAttachmentPreviewDialog
				preview={imageAttachmentPreview}
				onClose={() => setImageAttachmentPreview(null)}
			/>
			{pdfDialog}
		</Card>
	);
};

const TasksActive: FC = () => {
	const translation: Language | null = useContext(TranslationContext);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				flex: 1,
				minHeight: 0,
				display: "flex",
			}}
		>
			<TaskCard title={GetLanguageValueAsString(translation, "Auto.Features.Task.Pages.ActiveTasks.L1652C19.Active", "Active")} />
		</Box>
	);
};

export default TasksActive;
